"use client";

import { zodResolver } from "@hookform/resolvers/zod";
import { useRouter, useSearchParams } from "next/navigation";
import { useState } from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";

import { signIn } from "../services/api";

const OTP_LENGTH = 6;
const DEFAULT_ERROR_MESSAGE = "Giriş bilgilerinizi kontrol edin ve tekrar deneyin.";
const INVALID_MFA_ERROR_MESSAGE = "Geçersiz kod";

const signInSchema = z.object({
  tenant: z.string().min(1, "Zorunlu Alan"),
  email: z.string().email("Geçersiz email"),
  password: z.string().min(1, "Zorunlu Alan"),
  code: z.string().optional(),
});

type SignInFormValues = z.infer<typeof signInSchema>;

export function useSignInForm() {
  const router = useRouter();
  const searchParams = useSearchParams();
  const [isMfaStep, setIsMfaStep] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  // Self-service kayıt bittiğinde kullanıcı /login?tenant=<tenant_code> ile gelir;
  // müşteri kodunu elle yazdırmamak için alan önden doldurulur.
  const form = useForm<SignInFormValues>({
    resolver: zodResolver(signInSchema),
    defaultValues: {
      tenant: searchParams.get("tenant") ?? "",
      email: "",
      password: "",
      code: "",
    },
  });

  const codeValue = form.watch("code");
  const isCodeComplete = (codeValue ?? "").length === OTP_LENGTH;

  const onSubmit = form.handleSubmit(async (values) => {
    setErrorMessage("");

    try {
      const response = await signIn({
        tenant: values.tenant,
        email: values.email,
        password: values.password,
        code: values.code || undefined,
      });

      if (!response.ok) {
        if (response.code === "MFA_REQUIRED") {
          setIsMfaStep(true);
          return;
        }

        if (response.code === "INVALID_MFA") {
          setErrorMessage(INVALID_MFA_ERROR_MESSAGE);
          return;
        }

        setErrorMessage(DEFAULT_ERROR_MESSAGE);
        return;
      }

      router.push("/dashboard");
    } catch {
      setErrorMessage(DEFAULT_ERROR_MESSAGE);
    }
  });

  const subtitle = isMfaStep
    ? "E-posta adresinize gönderilen 6 haneli doğrulama kodunu girin."
    : "Hesabınıza giriş yapmak için gerekli bilgileri girin.";

  return {
    form,
    isMfaStep,
    errorMessage,
    subtitle,
    isCodeComplete,
    onSubmit,
    clearError: () => setErrorMessage(""),
  };
}
